import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapGlobe, type MapGlobeHandle } from "@app/components/MapGlobe.tsx";
import { useGame } from "@app/state/game.ts";
import type { GuessResponse } from "@app/api/client.ts";
import type { LatLon } from "@app/globe/globe.ts";
import { BAND_UI, categoryLabel, formatKm } from "@app/format.ts";
import { track } from "@app/analytics.ts";
import { captureBridge } from "@app/capture/bridge.ts";

export function Game() {
  const navigate = useNavigate();
  const round = useGame((s) => s.round);
  const guesses = useGame((s) => s.guesses);
  const position = useGame((s) => s.position);
  const completed = useGame((s) => s.completed);
  const loading = useGame((s) => s.loading);
  const error = useGame((s) => s.error);
  const load = useGame((s) => s.load);
  const submit = useGame((s) => s.submit);
  const advance = useGame((s) => s.advance);

  const [pin, setPin] = useState<LatLon | null>(null);
  const [reveal, setReveal] = useState<GuessResponse | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const globeRef = useRef<MapGlobeHandle>(null);

  useEffect(() => {
    void load();
  }, [load]);

  // Solo se salta al resultado si la partida ya estaba completada al entrar.
  // Tras la 5.ª pista el jugador ve su revelación y pulsa "Ver resultado".
  useEffect(() => {
    if (completed && !reveal && !busy) navigate("/resultado", { flushSync: true });
  }, [completed, reveal, busy, navigate]);

  const question = round?.questions[position - 1];

  // Eventos observables por el modo captura de reels (no-op fuera de él).
  useEffect(() => {
    if (question && !reveal) captureBridge.emit("clue_shown", { position, clue: question.clue });
  }, [question, position, reveal]);
  const answerPin = useMemo<LatLon | null>(
    () => (reveal ? { lat: reveal.result.answer.lat, lon: reveal.result.answer.lng } : null),
    [reveal],
  );

  const confirm = async () => {
    if (!pin || busy || reveal) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const response = await submit({ lat: pin.lat, lng: pin.lon });
      setReveal(response);
      if (response.completed) captureBridge.emit("round_complete", { totalScore: response.totalScore });
      track({
        name: "guess_submitted",
        roundId: round?.id ?? null,
        position,
        score: response.result.score,
      });
      setLive(
        `${response.result.score} puntos. A ${Math.round(response.result.distanceKm)} kilómetros. Respuesta: ${response.result.answer.name}.`,
      );
    } catch {
      // El intento es idempotente en el servidor: reintentar es seguro.
      setSubmitError("No se ha podido enviar tu pin. Comprueba la conexión y vuelve a confirmar.");
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (position >= 5) {
      track({ name: "round_complete", roundId: round?.id ?? null, score: useGame.getState().totalScore });
      // Navegación síncrona: React Router 7 la envuelve en startTransition y, con
      // actualizaciones urgentes del store en curso, podía seguir pintando el juego
      // con la URL ya en /resultado (visto en E2E de CI).
      navigate("/resultado", { flushSync: true });
      return;
    }
    advance();
    setReveal(null);
    setPin(null);
    setResetKey((k) => k + 1);
  };

  if (error) {
    return (
      <section className="screen">
        <p className="status" role="alert">
          {error}
        </p>
        <button type="button" className="cta" onClick={() => void load()}>
          Reintentar
        </button>
      </section>
    );
  }
  if (loading && !round) {
    return (
      <section className="screen">
        <p className="status">Cargando la ronda…</p>
      </section>
    );
  }
  if (!round || !question) return null;

  return (
    <section className="screen" aria-label="Partida">
      <div className="progress" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => {
          const progress = guesses.find((g) => g.position === i + 1);
          const cls = progress ? `is-${BAND_UI[progress.band].cls}` : i === position - 1 ? "is-now" : "";
          return <span key={i} className={cls} />;
        })}
      </div>
      <div className="clue">
        <div className="clue__k">
          <span>Pista {position} de 5</span>
          <span>{categoryLabel(question.category)}</span>
        </div>
        <p className="clue__text">{question.clue}</p>
      </div>
      <div className="globe">
        <MapGlobe
          ref={globeRef}
          guess={pin}
          answer={answerPin}
          resetKey={resetKey}
          onTap={(p) => {
            if (reveal) return;
            setPin(p);
            captureBridge.emit("guess_placed", { position, lat: p.lat, lng: p.lon });
          }}
          onRevealSettled={() => {
            if (!reveal) return;
            const r = reveal.result;
            captureBridge.emit("reveal_done", { position, score: r.score, band: r.band, distanceKm: r.distanceKm, answer: { lat: r.answer.lat, lng: r.answer.lng } });
          }}
          onEnter={() => void confirm()}
          ariaLabel="Globo terráqueo. Arrastra para girar y toca para colocar el pin. Con teclado: flechas para mover, más y menos para acercar, espacio para colocar el pin en el centro y Enter para confirmar."
        />
        <div className="globe__note">
          {reveal ? BAND_UI[reveal.result.band].note : pin ? "Confirma tu pin" : "Toca donde ocurrió"}
        </div>
        <div className="globe__zoom">
          <button
            type="button"
            className="ibtn"
            aria-label="Acercar"
            onClick={() => globeRef.current?.zoomIn()}
          >
            +
          </button>
          <button
            type="button"
            className="ibtn"
            aria-label="Alejar"
            onClick={() => globeRef.current?.zoomOut()}
          >
            −
          </button>
        </div>
        <div className="globe__attrib">© Natural Earth</div>
        {reveal ? (
          <div className="sheet">
            <div className="sheet__top">
              <div className="pts">
                {reveal.result.score}
                <small>/100</small>
              </div>
              <span className={`band band--${BAND_UI[reveal.result.band].cls}`}>
                {BAND_UI[reveal.result.band].label}
              </span>
            </div>
            <div className="place">{reveal.result.answer.name}</div>
            <div className="dist">Tu pin quedó a {formatKm(reveal.result.distanceKm)} km</div>
            <p className="expl">{reveal.result.answer.explanation}</p>
            <button type="button" className="cta" onClick={next} autoFocus>
              {position >= 5 ? "Ver resultado" : "Siguiente"}
            </button>
          </div>
        ) : null}
      </div>
      <div className="actions" hidden={Boolean(reveal)}>
        <button type="button" className="cta" disabled={!pin || busy} onClick={() => void confirm()}>
          Confirmar
        </button>
        <p className="actions__hint" role={submitError ? "alert" : undefined}>
          {submitError ?? (pin ? "Toca otro punto para mover el pin" : "Toca el mapa para colocar tu pin")}
        </p>
      </div>
      <div className="sr-only" aria-live="polite">
        {live}
      </div>
    </section>
  );
}
